'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';

const ANCHOR_SCROLL_REQUEST_EVENT = 'wcl:anchor-scroll-requested';

interface LazyViewportSectionProps {
  children: ReactNode;
  className?: string;
  eager?: boolean;
  fallbackClassName?: string;
  focusRootMargin?: string;
  focusOnViewport?: boolean;
  minHeight?: number;
  preserveMinHeightAfterRender?: boolean;
  rootMargin?: string;
}

export default function LazyViewportSection({
  children,
  className,
  eager = false,
  fallbackClassName,
  focusRootMargin = '0px 0px -40% 0px',
  focusOnViewport = false,
  minHeight = 1,
  preserveMinHeightAfterRender = false,
  rootMargin = '1200px 0px',
}: LazyViewportSectionProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(eager);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);

  useEffect(() => {
    if (eager) setShouldRender(true);
  }, [eager]);

  useEffect(() => {
    if (shouldRender) return;

    const renderForAnchorRequest = () => setShouldRender(true);

    window.addEventListener(ANCHOR_SCROLL_REQUEST_EVENT, renderForAnchorRequest);
    return () => {
      window.removeEventListener(ANCHOR_SCROLL_REQUEST_EVENT, renderForAnchorRequest);
    };
  }, [shouldRender]);

  useEffect(() => {
    if (shouldRender) return;
    const element = ref.current;
    if (!element) return;

    if (!('IntersectionObserver' in window)) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { rootMargin, threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  useEffect(() => {
    if (hasEnteredViewport) return;
    const element = ref.current;
    if (!element) return;

    if (!('IntersectionObserver' in window)) {
      setHasEnteredViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setHasEnteredViewport(true);
        observer.disconnect();
      },
      { rootMargin: focusRootMargin, threshold: 0 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [focusRootMargin, hasEnteredViewport]);

  return (
    <div
      ref={ref}
      className={cn(
        'lazy-viewport-section relative isolate',
        focusOnViewport &&
          shouldRender &&
          (hasEnteredViewport
            ? 'lazy-viewport-section--focused'
            : 'lazy-viewport-section--blurred'),
        shouldRender ? cn(fallbackClassName, className) : fallbackClassName,
      )}
      style={shouldRender && !preserveMinHeightAfterRender ? undefined : { minHeight }}
    >
      {shouldRender ? <div className="lazy-viewport-section__content">{children}</div> : null}
    </div>
  );
}
